/**
 * Custom Reviews Section - Okendo Integration
 * Implements the Figma design with dynamic data fetching from Okendo API
 */

class ReviewsSection {
  #productId;
  #productIds; // Array of all color variant product IDs
  #okendoUserId;
  #abortController;
  #reviewsData;
  #urlReviewIntent;
  #currentPage = 1;
  #itemsPerPage = 5;
  #currentOrderBy = 'date desc';
  #hasPhotoFilter = false;
  #allReviews = [];
  #nextUrl = null;
  #isLoadingMore = false;
  #renderedMediaCount = 0;
  #variantLabelsById = {};
  #productJsonByHandle = new Map();
  #pendingVariantLookups = new Map();
  #modalNavSyncTimeout = null;

  constructor(productId) {
    this.#productId = productId;
    this.#productIds = this.#getAllColorVariantProductIds(productId);
    this.#okendoUserId = this.#getOkendoUserId();
    this.#abortController = new AbortController();
    this.#urlReviewIntent = this.#getReviewIntentFromUrl();
    this.#variantLabelsById = this.#loadVariantLabelsMap();
    this.#reviewsData = {
      keywords: null,
      media: null,
      reviews: null,
      aggregate: null
    };
    this.#init();
  }

  #init() {
    this.#setupEventListeners();
    this.#loadReviewsData();
  }

  #loadVariantLabelsMap() {
    try {
      const script = document.querySelector('.reviews-variant-labels-json');
      if (!script?.textContent?.trim()) return { byVariantId: {}, byProductId: {} };
      const data = JSON.parse(script.textContent);
      if (!data || typeof data !== 'object') return { byVariantId: {}, byProductId: {} };

      // Backward-compatible: flat variant map or { byVariantId, byProductId }
      if (data.byVariantId || data.byProductId) {
        return {
          byVariantId: data.byVariantId && typeof data.byVariantId === 'object' ? data.byVariantId : {},
          byProductId: data.byProductId && typeof data.byProductId === 'object' ? data.byProductId : {}
        };
      }

      return { byVariantId: data, byProductId: {} };
    } catch (error) {
      console.warn('🔍 [OKENDO-REVIEWS] Error parsing reviews-variant-labels-json:', error);
      return { byVariantId: {}, byProductId: {} };
    }
  }

  #shopifyProductId(productId) {
    if (productId == null || productId === '') return '';
    return String(productId).replace(/^shopify-/i, '');
  }

  #escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #usableVariantTitle(value) {
    const title = String(value || '').trim();
    return title && title !== 'Default Title' ? title : '';
  }

  #isStyleOptionValue(value, productTitle = '') {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || normalized === 'default title') return true;

    const styleValues = new Set([
      'dining chair',
      'bar stool',
      'counter stool',
      'stool',
      'chair'
    ]);
    if (styleValues.has(normalized)) return true;

    const title = String(productTitle || '').trim().toLowerCase();
    return Boolean(title && (title === normalized || title.endsWith(` ${normalized}`)));
  }

  #colorFromParts({ color = '', variantTitle = '', option1 = '', productTitle = '' } = {}) {
    const candidates = [
      color,
      option1,
      String(variantTitle || '').split(/\s*\/\s*/)[0]
    ];

    for (const candidate of candidates) {
      const value = String(candidate || '').trim();
      if (value && !this.#isStyleOptionValue(value, productTitle)) {
        return value;
      }
    }

    return '';
  }

  #formatVariantLabelHtml(info) {
    const name = String(info?.productTitle || '').trim();
    const colorLabel = this.#colorFromParts({
      color: info?.color || '',
      variantTitle: info?.variantTitle || '',
      option1: info?.option1 || '',
      productTitle: name
    });

    if (name && colorLabel) {
      return `<span class="block">${this.#escapeHtml(name)}</span><span class="block">${this.#escapeHtml(colorLabel)}</span>`;
    }

    if (name) return `<span class="block">${this.#escapeHtml(name)}</span>`;
    return this.#escapeHtml(colorLabel);
  }

  /**
   * Current PDP product title from the reviews root (fallback for modal labels).
   * @returns {string}
   * @private
   */
  #getCurrentProductTitle() {
    const root = document.getElementById('reviews');
    const fromDataset = root?.dataset?.productTitle?.trim();
    if (fromDataset) return fromDataset;

    const heading = document.querySelector('h1.product__title, h1[data-product-title], .product-title h1, h1');
    return String(heading?.textContent || '').trim();
  }

  #getVariantLabelInfo(review) {
    const variantId = review?.variantId != null ? String(review.variantId) : '';
    const productId = this.#shopifyProductId(review?.productId);
    const byVariant = this.#variantLabelsById?.byVariantId || this.#variantLabelsById || {};
    const byProduct = this.#variantLabelsById?.byProductId || {};

    const fromVariant = variantId ? byVariant[variantId] : null;
    const fromProduct = productId ? byProduct[productId] : null;
    const productTitle = fromVariant?.productTitle
      || fromProduct?.productTitle
      || review?.productTitle
      || review?.productName
      || '';
    const variantTitle = this.#usableVariantTitle(fromVariant?.variantTitle);
    const handle = fromVariant?.handle || fromProduct?.handle || review?.productHandle || '';
    const color = this.#colorFromParts({
      color: fromVariant?.color || fromProduct?.color || '',
      variantTitle,
      productTitle
    }) || this.#colorFromHandle(handle, productTitle);
    const merged = {
      productTitle,
      variantTitle,
      color,
      handle,
      variantId,
      productId
    };

    return {
      ...merged,
      // Legacy Okendo reviews often lack variantId/handle; productId alone is enough to resolve via Okendo.
      needsLookup: Boolean(!merged.color && (productId || merged.handle))
    };
  }

  #renderVariantLabelHtml(review) {
    const info = this.#getVariantLabelInfo(review);
    const label = this.#formatVariantLabelHtml(info);
    if (!label && !info.needsLookup) return '';

    const attrs = [
      'data-review-variant-label',
      info.variantId ? `data-variant-id="${this.#escapeHtml(info.variantId)}"` : '',
      info.productId ? `data-product-id="${this.#escapeHtml(info.productId)}"` : '',
      info.handle ? `data-product-handle="${this.#escapeHtml(info.handle)}"` : '',
      info.productTitle ? `data-product-title="${this.#escapeHtml(info.productTitle)}"` : '',
      info.needsLookup ? 'data-needs-lookup="true"' : ''
    ].filter(Boolean).join(' ');

    return `
      <p class="text-sm text-[#21201f] mt-0.5" style="max-width: 10rem;" ${attrs}>${label}</p>
    `;
  }

  /**
   * Unconditional "Verified buyer" label below the variant title.
   * Review-date color (`text-ui-small opacity-70`), font size matches the variant title (`text-sm`).
   * @returns {string}
   * @private
   */
  #renderVerifiedBuyerLabelHtml() {
    const label =
      (window.reviewsStrings && window.reviewsStrings.verifiedBuyer) ||
      'Verified Buyer';
    return `<p class="text-ui-small text-sm opacity-70 mt-0.5" data-verified-buyer-label>${this.#escapeHtml(label)}</p>`;
  }

  async #fetchProductJson(handle) {
    if (!handle) return null;
    if (this.#productJsonByHandle.has(handle)) {
      return this.#productJsonByHandle.get(handle);
    }

    const existing = this.#pendingVariantLookups.get(handle);
    if (existing) return existing;

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      signal: this.#abortController.signal,
      headers: { Accept: 'application/json' }
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const data = await response.json();
        this.#productJsonByHandle.set(handle, data);
        return data;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('🔍 [OKENDO-REVIEWS] Variant label product fetch failed:', handle, error);
        }
        return null;
      })
      .finally(() => {
        this.#pendingVariantLookups.delete(handle);
      });

    this.#pendingVariantLookups.set(handle, request);
    return request;
  }

  #handleFromProductUrl(url) {
    if (!url) return '';
    try {
      const normalized = String(url).startsWith('//') ? `https:${url}` : String(url);
      const path = new URL(normalized, window.location.origin).pathname || '';
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  #handleize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #colorFromHandle(handle, productTitle = '') {
    if (!handle) return '';
    const base = this.#handleize(productTitle);
    let suffix = handle;
    if (base && handle.startsWith(`${base}-`)) {
      suffix = handle.slice(base.length + 1);
    }
    if (!suffix || suffix === handle) {
      // Fallback: drop first 3 handle segments when title-based base fails
      const parts = handle.split('-');
      if (parts.length > 3) suffix = parts.slice(3).join('-');
    }
    return suffix
      .split('-')
      .filter(Boolean)
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (index > 0 && (lower === 'and' || lower === 'or' || lower === 'the' || lower === 'of')) {
          return lower;
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  async #fetchOkendoProduct(productId) {
    const id = this.#shopifyProductId(productId);
    if (!id || !this.#okendoUserId) return null;
    const cacheKey = `okendo:${id}`;
    if (this.#productJsonByHandle.has(cacheKey)) {
      return this.#productJsonByHandle.get(cacheKey);
    }
    const existing = this.#pendingVariantLookups.get(cacheKey);
    if (existing) return existing;

    const request = fetch(
      `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${id}`,
      {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' }
      }
    )
      .then(async (response) => {
        if (!response.ok) return null;
        const data = await response.json();
        this.#productJsonByHandle.set(cacheKey, data);
        return data;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('🔍 [OKENDO-REVIEWS] Okendo product fetch failed:', id, error);
        }
        return null;
      })
      .finally(() => {
        this.#pendingVariantLookups.delete(cacheKey);
      });

    this.#pendingVariantLookups.set(cacheKey, request);
    return request;
  }

  async #resolveVariantLabelParts({ variantId = '', productId = '', handle = '', productTitle = '' } = {}) {
    const byVariant = this.#variantLabelsById.byVariantId || (this.#variantLabelsById.byVariantId = {});
    const byProduct = this.#variantLabelsById.byProductId || (this.#variantLabelsById.byProductId = {});

    let title = productTitle;
    let color = '';
    let variantTitle = '';
    let resolvedHandle = handle;

    if (variantId && this.#colorFromParts(byVariant[variantId] || {})) {
      return byVariant[variantId];
    }

    if (!resolvedHandle && productId) {
      const okendoProduct = await this.#fetchOkendoProduct(productId);
      const product = okendoProduct?.product;
      if (product) {
        title = title || product.name || '';
        resolvedHandle = this.#handleFromProductUrl(product.url);
      }
    }

    if (resolvedHandle) {
      const productJson = await this.#fetchProductJson(resolvedHandle);
      if (productJson) {
        title = title || productJson.title || '';
        variantTitle = this.#variantTitleFromProduct(productJson, variantId);
        color = this.#colorFromParts({
          color: this.#colorFromProductVariant(productJson, variantId),
          option1: productJson.variants?.[0]?.option1 || '',
          variantTitle,
          productTitle: title
        });
      }
    }

    if (!color && resolvedHandle) {
      color = this.#colorFromHandle(resolvedHandle, title);
    }

    if (!variantTitle && color) variantTitle = color;

    const info = {
      productId: productId || '',
      productTitle: title || '',
      variantTitle: variantTitle || '',
      color: color || '',
      handle: resolvedHandle || ''
    };

    if (variantId && (info.variantTitle || info.color)) byVariant[variantId] = info;
    if (productId && (info.variantTitle || info.color)) byProduct[productId] = info;
    return info;
  }

  #variantTitleFromProduct(productJson, variantId) {
    if (!productJson) return '';
    const variants = productJson.variants || [];
    const variant = (variantId && variants.find((item) => String(item.id) === String(variantId)))
      || (variants.length === 1 ? variants[0] : null);
    if (!variant) return '';
    if (this.#usableVariantTitle(variant.title)) return variant.title;
    return [variant.option1, variant.option2, variant.option3]
      .map((value) => String(value || '').trim())
      .filter((value) => value && value !== 'Default Title')
      .join(' / ');
  }

  #colorFromProductVariant(productJson, variantId) {
    if (!productJson) return '';
    const variants = productJson.variants || [];
    const variant = (variantId && variants.find((v) => String(v.id) === String(variantId)))
      || (variants.length === 1 ? variants[0] : null)
      || variants[0];
    if (!variant) return '';

    const options = productJson.options || [];
    const colorOptionIndex = options.findIndex((option) => {
      const n = String(option?.name || option || '').toLowerCase();
      return n === 'color' || n === 'colour';
    });
    const raw = colorOptionIndex === 0
      ? variant.option1
      : colorOptionIndex === 1
        ? variant.option2
        : colorOptionIndex === 2
          ? variant.option3
          : variant.option1 || (variant.title !== 'Default Title' ? variant.title.split(' / ')[0] : '');
    return this.#isStyleOptionValue(raw) ? '' : String(raw || '').trim();
  }

  async #hydrateVariantLabels(root = document) {
    const scope = root?.content instanceof DocumentFragment ? root.content : root;
    if (!scope?.querySelectorAll) return;

    const nodes = scope.querySelectorAll('[data-review-variant-label][data-needs-lookup="true"]');
    if (!nodes.length) return;

    await Promise.all(Array.from(nodes).map(async (node) => {
      const variantId = node.getAttribute('data-variant-id') || '';
      const productId = node.getAttribute('data-product-id') || '';
      const handle = node.getAttribute('data-product-handle') || '';
      const fallbackTitle = node.getAttribute('data-product-title') || '';

      const info = await this.#resolveVariantLabelParts({
        variantId,
        productId,
        handle,
        productTitle: fallbackTitle
      });

      const label = this.#formatVariantLabelHtml({
        productTitle: info.productTitle || fallbackTitle,
        variantTitle: info.variantTitle,
        color: info.color
      });
      if (!label) return;

      node.innerHTML = label;
      if (info.handle) node.setAttribute('data-product-handle', info.handle);
      if (info.productTitle) node.setAttribute('data-product-title', info.productTitle);
      if (info.color || info.variantTitle) node.removeAttribute('data-needs-lookup');
    }));
  }

  #getReviewIntentFromUrl() {
    try {
      const sp = new URLSearchParams(window.location.search);

      const shouldOpen =
        sp.get('write_review') === '1' ||
        sp.get('write_review') === 'true' ||
        sp.get('open_review') === '1' ||
        sp.get('open_review') === 'true' ||
        sp.get('review') === '1' ||
        sp.get('review') === 'true' ||
        sp.get('writeReview') === '1' ||
        sp.get('writeReview') === 'true';

      const rawRating =
        sp.get('rating') ??
        sp.get('stars') ??
        sp.get('review_rating') ??
        sp.get('reviewRating');

      let rating = null;
      if (rawRating !== null && rawRating !== undefined && rawRating !== '') {
        const n = Number(rawRating);
        if (Number.isFinite(n)) {
          rating = Math.max(1, Math.min(5, Math.round(n)));
        }
      }

      return { shouldOpen, rating };
    } catch {
      return { shouldOpen: false, rating: null };
    }
  }

  #buildOkendoWriteReviewUrl() {
    const url = new URL('https://okendo.reviews/');
    url.searchParams.set('subscriberId', '9e831136-29ed-41b1-8ee4-9fac44b0e5f7');
    url.searchParams.set('productId', `shopify-${this.#productId}`);

    const { rating } = this.#urlReviewIntent || {};
    const resolvedRating = rating || 5;

    // Okendo may ignore unknown params; we pass common ones for compatibility.
    url.searchParams.set('rating', String(resolvedRating));
    url.searchParams.set('stars', String(resolvedRating));

    return url.toString();
  }

  #maybeAutoOpenWriteReview() {
    const { shouldOpen } = this.#urlReviewIntent || {};
    if (!shouldOpen) return;

    // Prevent repeated auto-open on pagination / rerenders.
    const guardKey = 'okendo:write-review:auto-opened';
    try {
      if (window.sessionStorage?.getItem(guardKey) === '1') return;
      window.sessionStorage?.setItem(guardKey, '1');
    } catch {
      // If sessionStorage is blocked, we still proceed once per render.
    }

    const reviewsRoot = document.getElementById('reviews');
    if (reviewsRoot) {
      reviewsRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const link = document.querySelector('[data-write-review-link]');
    if (!link) return;

    // Allow paint/layout settle before triggering navigation.
    setTimeout(() => {
      link.click();
    }, 0);
  }

  #getOkendoUserId() {
    // Try to get Okendo user ID from various sources
    return window.Shopify?.shop?.metafields?.okendo?.userId || 
           window.okendo?.userId || 
           document.querySelector('meta[name="okendo-user-id"]')?.content || '';
  }

  /**
   * Get all color variant product IDs for the current product
   * @param {string} currentProductId - The current product ID
   * @returns {Array<string>} - Array of product IDs including the current one
   */
  #getAllColorVariantProductIds(currentProductId) {
    const productIds = new Set([String(currentProductId)]);
    
    try {
      // First, try to get from the reviews-specific script tag (most reliable)
      const reviewsVariantIdsScript = document.querySelector('.reviews-color-variant-ids-json');
      if (reviewsVariantIdsScript) {
        try {
          const productIdsArray = JSON.parse(reviewsVariantIdsScript.textContent);
          if (Array.isArray(productIdsArray)) {
            productIdsArray.forEach(id => {
              if (id) {
                productIds.add(String(id));
              }
            });
            return Array.from(productIds); // Return early if we found the script
          }
        } catch (e) {
          console.warn('🔍 [OKENDO-REVIEWS] Error parsing reviews-color-variant-ids-json:', e);
        }
      }
      
      // Fallback: Try to get color variant data from variant-color-json script
      const variantColorScript = document.querySelector('.variant-color-json');
      if (variantColorScript) {
        const variantData = JSON.parse(variantColorScript.textContent);
        
        // variantData structure can vary:
        // - Array of variant objects with product.id
        // - Array of objects with nested product structure
        if (Array.isArray(variantData)) {
          variantData.forEach(item => {
            // Try different possible structures
            let productId = null;
            
            if (item?.product?.id) {
              productId = item.product.id;
            } else if (item?.id) {
              // Might be a variant object, check if it has product_id
              productId = item.product_id;
            } else if (item?.variant?.product?.id) {
              productId = item.variant.product.id;
            }
            
            if (productId) {
              productIds.add(String(productId));
            }
          });
        } else if (variantData && typeof variantData === 'object') {
          // Handle object structure
          Object.values(variantData).forEach(item => {
            if (item?.product?.id) {
              productIds.add(String(item.product.id));
            } else if (item?.id) {
              productIds.add(String(item.id));
            }
          });
        }
      }
      
      // Also check if product data is available in window.Shopify
      if (window.Shopify?.analytics?.meta?.product?.variants) {
        const variants = window.Shopify.analytics.meta.product.variants;
        variants.forEach(variant => {
          if (variant?.product_id) {
            productIds.add(String(variant.product_id));
          }
        });
      }
      
      // Try to get from product JSON if available
      const productJson = document.querySelector('script[type="application/json"][data-product-json]');
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          if (productData?.variants) {
            productData.variants.forEach(variant => {
              if (variant?.product_id) {
                productIds.add(String(variant.product_id));
              }
            });
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    } catch (error) {
      console.warn('🔍 [OKENDO-REVIEWS] Error getting color variants:', error);
      // Fallback to just the current product ID
    }
    
    return Array.from(productIds);
  }

  #setupEventListeners() {
    const orderBySelect = document.getElementById('order-by-select');
    const hasPhotoFilter = document.getElementById('has-photo-filter');

    if (orderBySelect) {
      orderBySelect.addEventListener('change', () => this.#handleOrderByChange());
    }

    if (hasPhotoFilter) {
      hasPhotoFilter.addEventListener('change', () => this.#handlePhotoFilterChange());
    }
  }

  async #loadReviewsData() {
    try {
      this.#showLoadingState();
      
      if (!this.#okendoUserId) {
        const errorMsg = 'Okendo user ID not found. Check: window.Shopify.shop.metafields.okendo.userId, window.okendo.userId, or meta[name="okendo-user-id"]';
        console.error('🔍 [OKENDO-REVIEWS] Error:', errorMsg);
        this.#showError('Configuration Error', errorMsg);
        this.#hideLoadingState();
        return;
      }
      
      // STAGE 1: Fetch critical data first (reviews + aggregate for immediate render)
      // Fetch reviews and aggregate from all color variants
      const [reviewsDataArray, aggregateDataArray] = await Promise.all([
        Promise.all(this.#productIds.map(productId => 
          this.#fetchReviewsForProduct(productId, 10, this.#currentOrderBy)
        )),
        Promise.all(this.#productIds.map(productId => 
          this.#fetchReviewsAggregateForProduct(productId)
        ))
      ]);

      // Merge reviews from all variants, removing duplicates by review ID
      const mergedReviews = this.#mergeReviews(reviewsDataArray);
      this.#allReviews = mergedReviews.reviews;
      this.#nextUrl = mergedReviews.nextUrl;

      // Merge aggregate data from all variants
      const aggregateData = this.#mergeAggregateData(aggregateDataArray);

      this.#reviewsData = {
        keywords: null,
        media: null,
        reviews: { reviews: this.#allReviews, nextUrl: this.#nextUrl },
        aggregate: aggregateData
      };

      // Render immediately with critical data only
      this.#populateSummaryCards();
      this.#populateAttributesSummary();
      this.#populateReviewsList();
      this.#hideLoadingState();
      
      // STAGE 2: Fetch secondary data in background (non-blocking)
      Promise.all([
        Promise.all(this.#productIds.map(productId => 
          this.#fetchReviewsKeywordsForProduct(productId)
        )).then(keywordsArray => this.#mergeKeywordsData(keywordsArray)),
        Promise.all(this.#productIds.map(productId => 
          this.#fetchReviewsMediaForProduct(productId)
        )).then(mediaArray => this.#mergeMediaData(mediaArray))
      ]).then(([keywordsData, mediaData]) => {
        this.#reviewsData.keywords = keywordsData;
        this.#reviewsData.media = mediaData;
        
        // Update UI with secondary data
        this.#populateSummaryCards(); // Re-render if keywords affect quality topic
        this.#populateGallery();
        this.#createReviewsGalleryModal();
        this.#togglePhotoFilter();
      }).catch(error => {
        console.warn('🔍 [OKENDO-REVIEWS] Secondary data fetch failed:', error);
        // Non-critical, continue with what we have
      });
      
      // STAGE 3: Start background fetching of remaining reviews from all variants
        this.#fetchAllReviewsInBackground();
    } catch (error) {
      console.error('🔍 [OKENDO-REVIEWS] Error:', error);
      const errorType = error.name === 'AbortError' ? 'Request Cancelled' : 'API Error';
      const errorDetails = `${error.message}\n\nProduct ID: ${this.#productId}\nOkendo User ID: ${this.#okendoUserId || 'Not found'}`;
      this.#showError(errorType, errorDetails);
      this.#hideLoadingState();
    }
  }


  async #fetchReviewsKeywordsForProduct(productId) {
    if (!this.#okendoUserId) {
      throw new Error('Okendo user ID not found');
    }

    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${productId}/reviews_keywords`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        priority: 'low',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews keywords (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews keywords: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchReviewsKeywords() {
    return this.#fetchReviewsKeywordsForProduct(this.#productId);
  }

  async #fetchReviewsMediaForProduct(productId) {
    if (!this.#okendoUserId) {
      throw new Error('Okendo user ID not found');
    }

    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${productId}/review_media`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        priority: 'low',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews media (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews media: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchReviewsMedia() {
    return this.#fetchReviewsMediaForProduct(this.#productId);
  }

  async #fetchReviewsForProduct(productId, limit = 25, orderBy = 'date desc', nextUrl = null) {
    if (!this.#okendoUserId && !nextUrl) {
      throw new Error('Okendo user ID not found');
    }

    const url = nextUrl || `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${productId}/reviews?limit=${limit}&orderBy=${encodeURIComponent(orderBy)}`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        priority: nextUrl ? 'low' : 'high',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchReviews(limit = 25, orderBy = 'date desc', nextUrl = null) {
    return this.#fetchReviewsForProduct(this.#productId, limit, orderBy, nextUrl);
  }

  async #fetchAllReviewsInBackground() {
    if (this.#isLoadingMore) return;
    
    this.#isLoadingMore = true;
    
    try {
      // Fetch remaining reviews from all product variants in parallel
      const fetchPromises = this.#productIds.map(async (productId) => {
        let nextUrl = null;
        const productReviews = [];
        
        // First, get the initial batch if we haven't already
        try {
          const initialData = await this.#fetchReviewsForProduct(productId, 100, this.#currentOrderBy);
          productReviews.push(...(initialData?.reviews || []));
          nextUrl = initialData?.nextUrl || null;
        } catch (error) {
          console.warn(`🔍 [OKENDO-REVIEWS] Error fetching initial reviews for product ${productId}:`, error);
          return productReviews;
        }
        
        // Continue fetching paginated results
      while (nextUrl) {
        const url = nextUrl.startsWith('http') 
          ? nextUrl 
          : `https://api.okendo.io/v1${nextUrl}`;
        
          try {
        const response = await fetch(url, {
          signal: this.#abortController.signal,
          priority: 'low',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
              console.warn(`🔍 [OKENDO-REVIEWS] Failed to fetch more reviews for product ${productId}:`, response.status);
          break;
        }

        const data = await response.json();
        const newReviews = data?.reviews || [];
        nextUrl = data?.nextUrl || null;
        
            productReviews.push(...newReviews);
        
        // Use requestAnimationFrame for better timing (non-blocking)
        if (nextUrl) {
          await new Promise(resolve => requestAnimationFrame(resolve));
        }
          } catch (error) {
            if (error.name === 'AbortError') {
              throw error;
            }
            console.warn(`🔍 [OKENDO-REVIEWS] Error fetching more reviews for product ${productId}:`, error);
            break;
          }
        }
        
        return productReviews;
      });
      
      // Wait for all fetches to complete
      const allProductReviews = await Promise.all(fetchPromises);
      
      // Merge all reviews, removing duplicates
      const mergedReviews = this.#mergeReviews(
        allProductReviews.map(reviews => ({ reviews, nextUrl: null }))
      );
      
      // Update reviews array
      this.#allReviews = mergedReviews.reviews;
      
      // Update UI
      this.#updateUIWithNewReviews();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.warn('🔍 [OKENDO-REVIEWS] Error fetching all reviews:', error);
    } finally {
      this.#isLoadingMore = false;
    }
  }

  #updateUIWithNewReviews() {
    // Update pagination
    const filteredReviews = this.#hasPhotoFilter 
      ? this.#allReviews.filter(review => review.media && review.media.length > 0)
      : this.#allReviews;
    
    // Only update pagination if we're on page 1 (to avoid disrupting user navigation)
    if (this.#currentPage === 1) {
      this.#renderPagination(filteredReviews.length);
    }
    
    // Update gallery and modal as new reviews with photos are discovered
    this.#populateGallery();
    this.#updateReviewsGalleryModal();
  }

  async #fetchReviewsAggregateForProduct(productId) {
    if (!this.#okendoUserId) {
      throw new Error('Okendo user ID not found');
    }

    const url = `https://api.okendo.io/v1/stores/${this.#okendoUserId}/products/shopify-${productId}/review_aggregate`;
    
    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        priority: 'high',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Failed to fetch reviews aggregate (HTTP ${response.status}): ${errorText}\n\nURL: ${url}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new Error(`Network error fetching reviews aggregate: ${error.message}\n\nURL: ${url}`);
    }
  }

  async #fetchReviewsAggregate() {
    return this.#fetchReviewsAggregateForProduct(this.#productId);
  }

  #populateAllSections() {
    this.#populateAttributesSummary();
    this.#populateSummaryCards();
    this.#populateGallery();
    this.#populateReviewsList();
    this.#createReviewsGalleryModal();
    this.#togglePhotoFilter();
  }

  #togglePhotoFilter() {
    const filterControl = document.querySelector('.filter-control');
    if (!filterControl) return;
    
    // Check review_media endpoint data, not current page reviews
    const { media } = this.#reviewsData;
    const hasPhotos = media?.media && media.media.length > 0 && 
      media.media.some(item => {
        return item.imageUrls && 
               (item.imageUrls.largeUrl || item.imageUrls.fullSizeUrl || item.imageUrls.thumbnailUrl);
      });
    
    filterControl.style.display = hasPhotos ? 'flex' : 'none';
  }

  #populateAttributesSummary() {
    const attributesContainer = document.getElementById('reviews-attributes-summary');
    if (!attributesContainer) return;

    const { aggregate } = this.#reviewsData;
    
    // Check for attributes in aggregate data
    const attributes = aggregate?.reviewAggregate?.attributes || [];
    
    if (attributes.length === 0) {
      attributesContainer.style.display = 'none';
      return;
    }

    // Render attribute ratings (like "Price for Quality")
    const attributesHtml = attributes.map(attr => `
      <div class="attribute-summary-item">
        <div class="attribute-summary-label">${attr.name.toUpperCase()}</div>
        <div class="attribute-summary-bar">
          <div class="attribute-summary-track">
            <div class="attribute-summary-fill" style="width: ${(attr.averageRating / 5) * 100}%"></div>
          </div>
          <div class="attribute-summary-labels">
            <span>${attr.minLabel || 'Low'}</span>
            <span>${attr.maxLabel || 'High'}</span>
          </div>
        </div>
        <div class="attribute-summary-rating">${attr.averageRating.toFixed(1)} / 5</div>
      </div>
    `).join('');

    attributesContainer.innerHTML = attributesHtml;
    attributesContainer.style.display = 'block';
  }

  #populateSummaryCards() {
    const summaryCards = document.getElementById('reviews-summary-cards');
    if (!summaryCards) return;

    const { keywords, aggregate } = this.#reviewsData;
    
    // Get rating and count from script variable (same sources as _head-json-ld aggregateRating), else aggregate
    let rating = aggregate?.reviewAggregate?.averageRating ?? 0;
    let reviewCount = aggregate?.reviewAggregate?.reviewCount || 0;
    try {
      const summaryScript = document.querySelector('.reviews-summary-data-json');
      if (summaryScript) {
        const data = JSON.parse(summaryScript.textContent);
        if (data) {
          if (data.reviewRating !== null && data.reviewRating !== undefined) {
            const r = parseFloat(data.reviewRating);
            if (!Number.isNaN(r)) rating = r;
          }
          if (data.reviewCount !== null && data.reviewCount !== undefined) {
            const n = parseInt(data.reviewCount, 10);
            if (!Number.isNaN(n)) reviewCount = n;
          }
        }
      }
    } catch (e) {
      console.warn('🔍 [OKENDO-REVIEWS] Error parsing reviews-summary-data-json:', e);
    }
    
    // Get quality topic from keywords (or default to "PRICE FOR QUALITY")
    const qualityTopic = this.#getQualityTopic(keywords);
    
    // Calculate slider position (assuming rating out of 5)
    const sliderPosition = (rating / 5) * 100;
    
    // Determine if we have reviews
    const hasReviews = reviewCount > 0;
    
    // Create single summary card (1/3 width on desktop)
    const cardHtml = `
      <div class="flex flex-col gap-9 w-full md:w-1/3 mx-auto items-center">
        <!-- Rating Section -->
        <div class="flex flex-col gap-5 text-center">
          <h2>
            ${hasReviews ? `${rating.toFixed(1)} Rating` : 'No reviews yet'}
          </h2>
          ${hasReviews ? `
            <div class="flex gap-1.5 items-center justify-center text-ui-base">
              ${this.#generateRatingStars(rating, 5)}
              <p class="font-medium">${reviewCount} Reviews</p>
            </div>
          ` : ''}
        </div>
        
        <!-- Price for Quality Section (only show if reviews exist) -->
        ${hasReviews ? `
          <div class="flex flex-col gap-4 w-full">
            <p class="text-ui-title">
              ${qualityTopic}
            </p>
            <div class="flex flex-col gap-2 w-full relative">
              <!-- Slider Dot -->
              <div class="absolute w-1.5 h-1.5 bg-contrast rounded-full -top-0.75" style="left: calc(${sliderPosition}% - 3px)"></div>
              <!-- Slider Line -->
              <div class="h-0 w-full border-t border-primary"></div>
              <!-- Labels -->
              <div class="flex items-start justify-between w-full text-ui-small font-medium">
                <p>Expensive price</p>
                <p>Unbeatable price</p>
              </div>
            </div>
          </div>
        ` : ''}
        
        <!-- Write Review Button -->
        <a 
          class="border flex gap-2 items-center justify-center px-12 py-4 font-medium text-ui-title text-center whitespace-nowrap uppercase hover:bg-contrast hover:text-white transition-colors w-fit cursor-pointer"
          href="${this.#buildOkendoWriteReviewUrl()}"
          target="_blank"
          rel="noopener"
          data-write-review-link
        >
          WRITE A REVIEW
        </a>
      </div>
    `;

    summaryCards.innerHTML = cardHtml;
    summaryCards.style.display = 'block';
    this.#maybeAutoOpenWriteReview();
  }

  /**
   * Merge reviews from multiple product variants, removing duplicates
   * @param {Array} reviewsDataArray - Array of review data objects from different products
   * @returns {Object} - Merged reviews object with reviews array and nextUrl
   */
  #mergeReviews(reviewsDataArray) {
    const reviewMap = new Map();
    const allReviews = [];
    
    // Collect all reviews, using review ID as key to avoid duplicates
    reviewsDataArray.forEach(reviewsData => {
      const reviews = reviewsData?.reviews || [];
      reviews.forEach(review => {
        // Use review ID or a combination of reviewer + date + rating as unique key
        const reviewKey = review.id || `${review.reviewer?.displayName || 'anonymous'}-${review.dateCreated}-${review.rating}`;
        if (!reviewMap.has(reviewKey)) {
          reviewMap.set(reviewKey, review);
          allReviews.push(review);
        }
      });
    });
    
    // Sort by date (newest first) as default
    allReviews.sort((a, b) => {
      const dateA = new Date(a.dateCreated || 0);
      const dateB = new Date(b.dateCreated || 0);
      return dateB - dateA;
    });
    
    return {
      reviews: allReviews,
      nextUrl: null // No pagination after merge
    };
  }

  /**
   * Merge aggregate data from multiple product variants
   * @param {Array} aggregateDataArray - Array of aggregate data from different products
   * @returns {Object} - Merged aggregate data
   */
  #mergeAggregateData(aggregateDataArray) {
    const validAggregates = aggregateDataArray.filter(agg => agg?.reviewAggregate);
    
    if (validAggregates.length === 0) {
      return null;
    }
    
    // Combine review counts and calculate weighted average rating
    let totalReviewCount = 0;
    let totalRatingSum = 0;
    const attributeMap = new Map();
    
    validAggregates.forEach(agg => {
      const reviewAgg = agg.reviewAggregate;
      const reviewCount = reviewAgg.reviewCount || 0;
      const averageRating = reviewAgg.averageRating || 0;
      
      totalReviewCount += reviewCount;
      totalRatingSum += averageRating * reviewCount;
      
      // Merge attributes
      if (reviewAgg.attributes && Array.isArray(reviewAgg.attributes)) {
        reviewAgg.attributes.forEach(attr => {
          const key = attr.name;
          if (!attributeMap.has(key)) {
            attributeMap.set(key, {
              name: attr.name,
              totalRating: 0,
              totalCount: 0,
              minLabel: attr.minLabel,
              maxLabel: attr.maxLabel
            });
          }
          
          const existing = attributeMap.get(key);
          const attrValue = attr.averageRating || 0;
          existing.totalRating += attrValue * reviewCount;
          existing.totalCount += reviewCount;
        });
      }
    });
    
    // Calculate merged average rating
    const mergedAverageRating = totalReviewCount > 0 
      ? totalRatingSum / totalReviewCount 
      : 0;
    
    // Calculate merged attributes
    const mergedAttributes = Array.from(attributeMap.values()).map(attr => ({
      name: attr.name,
      averageRating: attr.totalCount > 0 ? attr.totalRating / attr.totalCount : 0,
      minLabel: attr.minLabel,
      maxLabel: attr.maxLabel
    }));
    
    return {
      reviewAggregate: {
        reviewCount: totalReviewCount,
        averageRating: mergedAverageRating,
        attributes: mergedAttributes
      }
    };
  }

  /**
   * Merge keywords data from multiple product variants
   * @param {Array} keywordsDataArray - Array of keywords data from different products
   * @returns {Object} - Merged keywords data
   */
  #mergeKeywordsData(keywordsDataArray) {
    const validKeywords = keywordsDataArray.filter(kw => kw?.reviewsKeywords);
    
    if (validKeywords.length === 0) {
      return null;
    }
    
    // Combine keyword topics, merging counts
    const topicMap = new Map();
    
    validKeywords.forEach(kw => {
      const topics = kw.reviewsKeywords?.keywordTopics || [];
      topics.forEach(topic => {
        const key = topic.topic.toLowerCase();
        if (!topicMap.has(key)) {
          topicMap.set(key, {
            topic: topic.topic,
            count: 0
          });
        }
        topicMap.get(key).count += topic.count || 0;
      });
    });
    
    const mergedTopics = Array.from(topicMap.values())
      .sort((a, b) => b.count - a.count);
    
    return {
      reviewsKeywords: {
        keywordTopics: mergedTopics
      }
    };
  }

  /**
   * Merge media data from multiple product variants
   * @param {Array} mediaDataArray - Array of media data from different products
   * @returns {Object} - Merged media data
   */
  #mergeMediaData(mediaDataArray) {
    const validMedia = mediaDataArray.filter(m => m?.media);
    
    if (validMedia.length === 0) {
      return null;
    }
    
    // Combine all media items, removing duplicates by URL
    const mediaMap = new Map();
    const allMedia = [];
    
    validMedia.forEach(mediaData => {
      const mediaItems = mediaData.media || [];
      mediaItems.forEach(item => {
        // Use image URL as unique key
        const imageUrl = item.imageUrls?.largeUrl || 
                        item.imageUrls?.fullSizeUrl || 
                        item.imageUrls?.thumbnailUrl;
        
        if (imageUrl && !mediaMap.has(imageUrl)) {
          mediaMap.set(imageUrl, true);
          allMedia.push(item);
        }
      });
    });
    
    return {
      media: allMedia
    };
  }

  /**
   * Get the primary media item from a review's media array
   * Prioritizes video over images, returns first image if no video
   * @param {Array} media - Array of media items from a review
   * @returns {Object|null} - Primary media item or null
   */
  #getPrimaryMedia(media) {
    if (!media || media.length === 0) return null;
    
    // First, try to find a video (prioritize video over images)
    const video = media.find(item => item.type === 'video');
    
    if (video) return video;
    
    // If no video, return first image
    const firstImage = media.find(item => item.type === 'image');
    
    return firstImage || null;
  }

  #getReviewMediaItems(review) {
    if (!review) return [];
    if (Array.isArray(review.media) && review.media.length > 0) return review.media;
    return [];
  }

  #buildReviewsWithPhotos() {
    // Get media from review_media endpoint
    const { media: mediaData } = this.#reviewsData;
    const mediaItems = mediaData?.media || [];
    
    // Create a map of review ID to media items for quick lookup
    // Media items from Okendo API can have different structures
    const mediaByReviewId = new Map();
    mediaItems.forEach(mediaItem => {
      // Try multiple possible structures for review ID
      const reviewId = mediaItem.reviewId || 
                      mediaItem.review_id || 
                      mediaItem.review?.id;
      
      // Also try matching by review object if it exists
      if (!reviewId && mediaItem.review) {
        const reviewIdFromReview = mediaItem.review.id || 
                                  mediaItem.review.reviewId ||
                                  mediaItem.review.review_id;
        if (reviewIdFromReview) {
          const id = String(reviewIdFromReview);
          if (!mediaByReviewId.has(id)) {
            mediaByReviewId.set(id, []);
          }
          mediaByReviewId.get(id).push(mediaItem);
          return;
        }
      }
      
      if (reviewId) {
        const id = String(reviewId);
        if (!mediaByReviewId.has(id)) {
          mediaByReviewId.set(id, []);
        }
        mediaByReviewId.get(id).push(mediaItem);
      }
    });
    
    // Build reviews with photos - check both review.media and mediaByReviewId
    const reviewsWithPhotos = this.#allReviews
      .map(review => {
        // Get media from review object (if present) or from mediaByReviewId map
        let reviewMedia = [];
        
        // First, check if review has media property directly
        if (review.media && Array.isArray(review.media) && review.media.length > 0) {
          reviewMedia = review.media;
        } else {
          // Try to match by review ID (try both string and number)
          const reviewId = String(review.id || '');
          reviewMedia = mediaByReviewId.get(reviewId) || [];
        }
        
        // Only include reviews that have media
        if (reviewMedia.length === 0) {
          return null;
        }
        
        const primaryMedia = this.#getPrimaryMedia(reviewMedia);
        if (!primaryMedia) {
          return null;
        }
        
        // Keep product/variant fields so the gallery lightbox can render titles.
        return {
          id: review.id,
          reviewer: review.reviewer,
          dateCreated: review.dateCreated,
          rating: review.rating,
          title: review.title,
          body: review.body,
          media: reviewMedia,
          primaryMedia: primaryMedia,
          variantId: review.variantId,
          productId: review.productId,
          productName: review.productName || review.productTitle || '',
          productHandle: review.productHandle || review.handle || '',
          productTitle: review.productTitle || review.productName || ''
        };
      })
      .filter(review => review !== null); // Remove null entries
    
    return reviewsWithPhotos;
  }

  /**
   * One entry per image/video in gallery + modal order (review text repeated on each slide).
   * @param {Array} reviewsWithPhotos
   * @returns {{ review: object, mediaItem: object }[]}
   */
  #buildGalleryMediaSlides(reviewsWithPhotos) {
    const slides = [];
    if (!Array.isArray(reviewsWithPhotos)) return slides;
    for (const review of reviewsWithPhotos) {
      const items = Array.isArray(review.media) ? review.media : [];
      for (const mediaItem of items) {
        if (!mediaItem) continue;
        const t = mediaItem.type;
        if (t !== 'image' && t !== 'video') continue;
        slides.push({ review, mediaItem });
      }
    }
    return slides;
  }

  #createReviewModalCard(review, featuredMedia, index) {
    const reviewerName = (review.reviewer?.displayName || 'Anonymous')
      .split(' ')[0].toUpperCase();
    const reviewDate = this.#formatDateShort(review.dateCreated);

    // Check if featured media is a video
    const isVideo = featuredMedia.type === 'video';

    // Get URLs based on media type
    const videoUrl = isVideo ? featuredMedia.fullSizeUrl : null;
    const imageUrl = isVideo
      ? (featuredMedia.videoPosterUrl || featuredMedia.thumbnailUrl)
      : (featuredMedia.fullSizeUrl || featuredMedia.largeUrl);
    const imageAlt = !isVideo && featuredMedia.alt
      ? featuredMedia.alt
      : `Review by ${reviewerName}`;

    // Ensure lightbox always has a product title (review data or current PDP).
    const reviewForLabel = {
      ...review,
      productName: review?.productName || review?.productTitle || this.#getCurrentProductTitle(),
      productTitle: review?.productTitle || review?.productName || this.#getCurrentProductTitle()
    };
    
    return `
      <div class="flex flex-col lg:grid grid-cols-2 snap-start w-fit min-h-full" data-slide-index="${index}">
        <!-- Left: Media (Video or Image) -->
        <div class="bg-minimal flex items-center aspect-square lg:aspect-[0.8] lg:sticky lg:top-0">
          ${isVideo && videoUrl ? `
            <video 
              src="${videoUrl}" 
              controls
              class="mix-blend-darken h-full w-full object-cover"
              poster="${imageUrl || ''}"
            >
              Your browser does not support the video tag.
            </video>
          ` : `
          <img 
            src="${imageUrl}" 
            alt="${imageAlt}"
            class="mix-blend-darken h-full w-full object-cover"
            loading="lazy"
          />
          `}
        </div>
        
        <!-- Right: Review Details -->
        <div class="flex flex-col gap-4 p-4 lg:p-14 overflow-y-auto max-h-[calc(100vh-2rem)] lg:max-h-none">
          <div class="flex flex-col gap-1">
            <p class="text-ui-title flex justify-between">${reviewerName}<span class="text-ui-small opacity-70">${reviewDate}</span></p>
            ${this.#renderVariantLabelHtml(reviewForLabel)}
            ${this.#renderVerifiedBuyerLabelHtml()}
          </div>
          
          <div class="flex flex-col gap-2">
            <div class="text-ui-small">${this.#generateRatingStars(review.rating, 5)}</div>
            <p class="font-normal text-sm leading-[1.5]">${review.body || 'No review text available.'}</p>
          </div>
        </div>
      </div>
    `;
  }

  #createReviewsGalleryModal() {
    const modalId = 'reviews-gallery-modal';
    const sliderId = 'reviews-modal-slider';
    
    // Check if modal already exists - if so, just update the content
    const existingModal = document.getElementById(modalId);
    if (existingModal) {
      // Modal exists, update its content
      const slider = document.querySelector(`electric-slider[data-id="${sliderId}"]`);
      const sliderFeed = slider?.querySelector(`[data-slider="${sliderId}"]`);
      if (slider && sliderFeed) {
        const reviewsWithPhotos = this.#buildReviewsWithPhotos();
        const slides = this.#buildGalleryMediaSlides(reviewsWithPhotos);
        if (slides.length > 0) {
          const sliderCards = slides.map(({ review, mediaItem }, index) =>
            this.#createReviewModalCard(review, mediaItem, index)
          ).join('');
          sliderFeed.innerHTML = sliderCards;
          this.#hydrateVariantLabels(sliderFeed);
          if (typeof slider.handleContentChange === 'function') {
            slider.handleContentChange();
          }
        }
      }
      return;
    }
    
    // Build slider feed with review cards
    const reviewsWithPhotos = this.#buildReviewsWithPhotos();
    const slides = this.#buildGalleryMediaSlides(reviewsWithPhotos);

    if (slides.length === 0) {
      return; // No reviews with photos, don't create modal
    }

    const sliderCards = slides.map(({ review, mediaItem }, index) =>
      this.#createReviewModalCard(review, mediaItem, index)
    ).join('');
    
    // Create modal template structure (similar to m--modal.liquid)
    const modalTemplate = document.createElement('template');
    modalTemplate.setAttribute('data-modal-content', modalId);
    modalTemplate.setAttribute('data-content-class', 'w-fit');
    
    // Create slider navigation (m--slider-navigation equivalent)
    const sliderNavigation = `
      <div class="max-lg:top-[110vw] h-9 max-lg:text-contrast grid grid-cols-[1fr_auto] items-center w-full px-0! absolute lg:bottom-3 right-3 z-10">
        <div class="gap-x-1 flex col-start-2">
          <button name="previous" aria-label="previous" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M10.6667 15L8 12M8 12L10.6667 9M8 12H16" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
          <button name="next" aria-label="next" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
            <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor"/>
              <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
              <path d="M13.3333 9L16 12M16 12L13.3333 15M16 12H8" stroke="currentColor" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    // Create slider feed wrapper
    const sliderFeed = `
      <div 
        data-slider="${sliderId}"
        class="flex overflow-y-hidden overflow-x-scroll justify-start electric-slider__feed hide-scroll-bar snap-x snap-mandatory scroll-smooth [&_>_*]:w-column-basis [&_>_*]:shrink-0 [&_>_*]:snap-start gap-x-0 w-full h-full"
        tabindex="0"
      >
        ${sliderCards}
      </div>
    `;
    
    // Create modal content wrapper
    const modalContent = `
      <style>
        [data-slider="${sliderId}"] {
          --columns: 1;
          --md-columns: 1;
          --lg-columns: 1;
          --gap: 0px;
        }
      </style>
      <div class="relative bg-white shadow-xl w-full max-w-[90vw] max-h-[90vh] overflow-y-auto lg:overflow-hidden lg:aspect-[1.6] lg:max-w-5xl">
        <electric-slider
          data-id="${sliderId}"
          class="electric-slider group/snap relative max-w-full mr-auto flex flex-col gap-y-2 w-full h-full"
          data-slider-step-by="page"
        >
          ${sliderNavigation}
          ${sliderFeed}
        </electric-slider>
        <button 
          type="button" 
          class="absolute cursor-pointer top-4 right-4 p-1 text-black hover:text-black/70 bg-primary/50 rounded-full focus:outline-none focus:ring-offset-2 focus:ring-primary z-10"
          data-modal-close
        >
          <span class="sr-only">Close</span>
          <svg 
            class="shrink-0 stroke-md lg:stroke-lg" 
            width="1.5rem" 
            height="1.5rem" 
            viewBox="0 0 24 24" 
            fill="none" 
            aria-hidden="true"
          >
            <path d="M18 6L6 18" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;
    
    modalTemplate.innerHTML = modalContent;
    
    // Create electric-modal element
    const modalElement = document.createElement('electric-modal');
    modalElement.id = modalId;
    
    // Append template to document
    document.body.appendChild(modalTemplate);
    document.body.appendChild(modalElement);
    this.#hydrateVariantLabels(modalTemplate);

    // Setup navigation handler after modal is created
    this.#setupModalNavigation();
  }

  /**
   * Sync lightbox prev/next disabled + aria-disabled from the live slide position.
   * Covers arrows, keyboard, swipe, and open-at-index paths.
   * @param {HTMLElement|null|undefined} slider
   * @private
   */
  #syncReviewsModalNavState(slider) {
    if (!slider || typeof slider.updateEdgeStates !== 'function') return;
    try {
      slider.updateEdgeStates();
    } catch (error) {
      console.warn('🔍 [OKENDO-REVIEWS] syncReviewsModalNavState:', error);
    }
  }

  #setupModalNavigation() {
    const modalId = 'reviews-gallery-modal';
    const sliderId = 'reviews-modal-slider';
    
    // Listen for modal:open event on document (modal dispatches to document)
    document.addEventListener('modal:open', (event) => {
      // Only handle events for our modal
      if (event.detail?.modalId !== modalId) return;
      
      // Check for slide-index (from data-modal-slide-index attribute)
      // The modal converts data-modal-* attributes to keys by removing the prefix
      let slideIndex = event.detail?.data?.['slide-index'] ?? 
                      event.detail?.data?.slideIndex ?? 
                      event.detail?.data?.['review-index'] ?? 
                      event.detail?.data?.reviewIndex;
      
      if (slideIndex === undefined || slideIndex === null) {
        console.warn('🔍 [OKENDO-REVIEWS] No slide index found in modal open event');
        return;
      }
      
      const index = parseInt(slideIndex);
      if (isNaN(index) || index < 0) {
        console.warn('🔍 [OKENDO-REVIEWS] Invalid slide index:', slideIndex);
        return;
      }
      
      // Wait for slider to be injected and initialized
      // The template content is injected into .electric-modal-content
      const modalContent = document.querySelector('.electric-modal-content');
      if (!modalContent) {
        console.warn('🔍 [OKENDO-REVIEWS] Modal content container not found');
        return;
      }
      
      // Poll for slider to exist and be initialized
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      
      const findAndNavigate = () => {
        attempts++;
        
        // Find slider inside the modal content (it's injected there)
        const slider = modalContent.querySelector(`electric-slider[data-id="${sliderId}"]`);
        
        if (!slider) {
          if (attempts < maxAttempts) {
            setTimeout(findAndNavigate, 100);
          } else {
            console.warn('🔍 [OKENDO-REVIEWS] Slider not found after', maxAttempts, 'attempts');
          }
          return;
        }
      
        // Check if slider is initialized (has navigateSlider method)
        if (typeof slider.navigateSlider !== 'function') {
          if (attempts < maxAttempts) {
            setTimeout(findAndNavigate, 100);
          } else {
            console.warn('🔍 [OKENDO-REVIEWS] Slider not initialized after', maxAttempts, 'attempts');
          }
          return;
        }
        
        // Slider is ready, navigate to the slide
        try {
          slider.navigateSlider(index, 'instant');
          this.#hydrateVariantLabels(modalContent);
          requestAnimationFrame(() => this.#syncReviewsModalNavState(slider));
          setTimeout(() => this.#syncReviewsModalNavState(slider), 120);
          this.#bindReviewsModalNavListeners(slider, modalContent);
        } catch (error) {
          console.error('🔍 [OKENDO-REVIEWS] Error navigating slider:', error);
        }
      };
    
      // Start polling immediately
      findAndNavigate();
    }, { signal: this.#abortController.signal });
  }

  /**
   * Keep lightbox arrow boundary state in sync for every navigation path.
   * @param {HTMLElement} slider
   * @param {ParentNode} modalContent
   * @private
   */
  #bindReviewsModalNavListeners(slider, modalContent) {
    if (!slider || slider.dataset.reviewsNavBound === 'true') return;
    slider.dataset.reviewsNavBound = 'true';

    const feed = slider.querySelector('.electric-slider__feed');
    const sync = () => this.#syncReviewsModalNavState(slider);
    const { signal } = this.#abortController;

    feed?.addEventListener('scroll', () => {
      clearTimeout(this.#modalNavSyncTimeout);
      this.#modalNavSyncTimeout = setTimeout(sync, 50);
    }, { passive: true, signal });
    feed?.addEventListener('scrollend', sync, { signal });

    const prevBtn = slider.querySelector('button[name="previous"]')
      || modalContent.querySelector(`button[name="previous"][data-slider-target="reviews-modal-slider"]`);
    const nextBtn = slider.querySelector('button[name="next"]')
      || modalContent.querySelector(`button[name="next"][data-slider-target="reviews-modal-slider"]`);

    prevBtn?.addEventListener('click', () => setTimeout(sync, 160), { signal });
    nextBtn?.addEventListener('click', () => setTimeout(sync, 160), { signal });
  }

  #updateReviewsGalleryModal() {
    const modalId = 'reviews-gallery-modal';
    const sliderId = 'reviews-modal-slider';
    
    const modal = document.getElementById(modalId);
    if (!modal) {
      // Modal doesn't exist, create it
      this.#createReviewsGalleryModal();
      return;
    }
    
    const slider = document.querySelector(`electric-slider[data-id="${sliderId}"]`);
    if (!slider) {
      // Slider not ready yet, wait a bit and try again
      setTimeout(() => this.#updateReviewsGalleryModal(), 100);
      return;
    }
    
    const sliderFeed = slider.querySelector(`[data-slider="${sliderId}"]`);
    if (!sliderFeed) {
      // Slider feed not found, wait a bit and try again
      setTimeout(() => this.#updateReviewsGalleryModal(), 100);
      return;
    }
    
    // Build updated reviews with photos
    const reviewsWithPhotos = this.#buildReviewsWithPhotos();
    
    if (reviewsWithPhotos.length === 0) return;

    const slides = this.#buildGalleryMediaSlides(reviewsWithPhotos);
    if (slides.length === 0) return;

    // Create new slider cards (one slide per image/video)
    const sliderCards = slides.map(({ review, mediaItem }, index) =>
      this.#createReviewModalCard(review, mediaItem, index)
    ).join('');
    
    // Update slider feed
    sliderFeed.innerHTML = sliderCards;
    this.#hydrateVariantLabels(sliderFeed);
    
    // Reinitialize slider if needed
    if (typeof slider.handleContentChange === 'function') {
      slider.handleContentChange();
    }
  }

  #populateGallery() {
    const galleryImages = document.getElementById('gallery-images');
    const gallerySection = document.getElementById('reviews-gallery');
    if (!galleryImages || !gallerySection) return;

    // Build reviews with photos
    const reviewsWithPhotos = this.#buildReviewsWithPhotos();
    
    if (reviewsWithPhotos.length === 0) {
      gallerySection.style.display = 'none';
      return;
    }

    const slides = this.#buildGalleryMediaSlides(reviewsWithPhotos);

    if (slides.length === 0) {
      gallerySection.style.display = 'none';
      return;
    }

    // Only render items that haven't been rendered yet
    const mediaToRender = slides.slice(this.#renderedMediaCount);
    
    // If all items are already rendered, don't re-render
    if (mediaToRender.length === 0) {
      return;
    }

    const galleryItems = mediaToRender.map((slide, relativeIndex) => {
      const { mediaItem } = slide;
      const modalSlideIndex = this.#renderedMediaCount + relativeIndex;
      const isVideo = mediaItem.type === 'video';
      // Okendo thumbnailUrl is often very low-res; prefer large/full for gallery tiles.
      const thumbnailUrl = isVideo
        ? (mediaItem.videoPosterUrl || mediaItem.largeUrl || mediaItem.fullSizeUrl || mediaItem.thumbnailUrl)
        : (mediaItem.largeUrl || mediaItem.fullSizeUrl || mediaItem.thumbnailUrl);
      const fullSizeUrl = mediaItem.fullSizeUrl || mediaItem.largeUrl || mediaItem.thumbnailUrl;
      const videoUrl = isVideo ? (mediaItem.fullSizeUrl || mediaItem.largeUrl) : null;
      const imageAlt = !isVideo && mediaItem.alt ? mediaItem.alt : `Review image ${modalSlideIndex + 1}`;
      return { modalSlideIndex, isVideo, thumbnailUrl, fullSizeUrl, videoUrl, imageAlt };
    });

    const imagesHtml = galleryItems.map((item) => {
      const { modalSlideIndex, isVideo, thumbnailUrl, fullSizeUrl, videoUrl, imageAlt } = item;

      return `
      <div 
        class="aspect-square w-50 shrink-0 overflow-hidden snap-start cursor-pointer hover:opacity-90 transition-opacity" 
        data-modal-trigger="reviews-gallery-modal"
        data-modal-slide-index="${modalSlideIndex}"
        data-full-url="${fullSizeUrl}"
        role="button"
        tabindex="0"
        aria-label="View review media ${modalSlideIndex + 1}"
      >
        ${isVideo && videoUrl ? `
          <video 
            src="${videoUrl}" 
            class="w-full h-full object-cover object-center"
            poster="${thumbnailUrl || ''}"
            muted
            loop
            playsinline
          >
          </video>
        ` : `
        <img 
          src="${thumbnailUrl}" 
          alt="${imageAlt}" 
          width="200"
          height="200"
          loading="lazy"
          class="w-full h-full object-cover object-center"
          onerror="this.parentElement.style.display='none'"
        >
        `}
      </div>
    `;
    }).join('');

    // If this is the initial render, replace innerHTML; otherwise append
    if (this.#renderedMediaCount === 0) {
      galleryImages.innerHTML = imagesHtml;
      gallerySection.style.display = 'flex';
    } else {
      // Append new items
      galleryImages.insertAdjacentHTML('beforeend', imagesHtml);
    }
    
    // Force a layout recalculation by accessing offsetHeight
    void galleryImages.offsetHeight;
    
    this.#renderedMediaCount += mediaToRender.length;
    
    // Trigger slider to recalculate scrollability after content is added
    // The slider has a MutationObserver, but we need to ensure it updates after images load
    const slider = document.querySelector('electric-slider[data-id="reviews-gallery-slider"]');
    if (!slider) {
      console.warn('🔍 [OKENDO-REVIEWS] Slider not found for scrollability update');
      return;
    }
    
    // Wait for DOM to update and images to load
    requestAnimationFrame(() => {
      // Force handleContentChange to recache items
      if (typeof slider.handleContentChange === 'function') {
        slider.handleContentChange();
      }
      
      // Wait for images to load before checking scrollability
      const images = galleryImages.querySelectorAll('img, video');
      if (images.length > 0) {
        let loadedCount = 0;
        const totalImages = images.length;
        let allLoaded = false;
        
        const checkAllLoaded = () => {
          if (allLoaded) return; // Prevent multiple calls
          
          loadedCount++;
          if (loadedCount >= totalImages) {
            allLoaded = true;
            // All images loaded, force update scrollability multiple times to ensure it sticks
            requestAnimationFrame(() => {
              if (typeof slider.updateScrollability === 'function') {
                slider.updateScrollability();
              }
              // Also call updateUI which includes updateScrollability
              if (typeof slider.updateUI === 'function') {
                slider.updateUI();
              }
              // One more check after a short delay to ensure layout is settled
              setTimeout(() => {
                if (typeof slider.updateScrollability === 'function') {
                  slider.updateScrollability();
                }
              }, 200);
            });
          }
        };
        
        images.forEach(img => {
          if (img.complete || (img.readyState === 4 && img.tagName === 'VIDEO')) {
            checkAllLoaded();
          } else {
            img.addEventListener('load', checkAllLoaded, { once: true });
            img.addEventListener('error', checkAllLoaded, { once: true });
            // For videos, also check loadeddata event
            if (img.tagName === 'VIDEO') {
              img.addEventListener('loadeddata', checkAllLoaded, { once: true });
            }
          }
        });
      } else {
        // No images, update after a delay
        setTimeout(() => {
          if (typeof slider.updateScrollability === 'function') {
            slider.updateScrollability();
          }
          if (typeof slider.updateUI === 'function') {
            slider.updateUI();
          }
        }, 200);
      }
    });
  }

  #populateReviewsList() {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    if (!reviewsList || !reviewsSection) return;

    const { reviews: reviewsResponse } = this.#reviewsData;
    
    // The /reviews endpoint returns { reviews: [], areReviewsGrouped: bool }
    const reviews = reviewsResponse?.reviews || [];
    this.#allReviews = reviews;
    
    if (reviews.length === 0) {
      const emptyMsg = (window.reviewsStrings && window.reviewsStrings.emptyHtml) || 'We want to know what you think. Be the first to leave a review.';
      reviewsList.innerHTML = `<p class="text-center">${emptyMsg}</p>`;
      reviewsSection.style.display = 'block';
      
      // Hide controls when no reviews
      const reviewsControls = document.querySelector('.reviews-controls');
      if (reviewsControls) {
        reviewsControls.style.display = 'none';
      }
      return;
    }

    this.#renderReviews();
  }

  #renderPagination(totalReviews) {
    const paginationContainer = document.getElementById('reviews-pagination');
    if (!paginationContainer) return;

    const totalPages = Math.ceil(totalReviews / this.#itemsPerPage);
    
    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      paginationContainer.style.display = 'none';
      return;
    }

    let paginationHtml = '<div class="pagination-wrapper"><div class="pagination-controls">';
    
    // Previous button
    if (this.#currentPage > 1) {
      paginationHtml += `<button class="pagination-btn pagination-prev" data-page="${this.#currentPage - 1}" aria-label="Previous page">
        ${window.icons?.circle_arrow_l ?? ''}
      </button>`;
    } else {
      paginationHtml += `<button class="pagination-btn pagination-prev opacity-50" disabled aria-label="Previous page">
        ${window.icons?.circle_arrow_l ?? ''}
      </button>`;
    }
    
    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (i === this.#currentPage) {
        paginationHtml += `<button class="pagination-btn pagination-number" data-page="${i}" aria-label="Page ${i}" aria-current="page">${i}</button>`;
      } else if (i === 1 || i === totalPages || (i >= this.#currentPage - 1 && i <= this.#currentPage + 1)) {
        paginationHtml += `<button class="pagination-btn pagination-number cursor-pointer opacity-70 hover:opacity-100" data-page="${i}" aria-label="Page ${i}">${i}</button>`;
      } else if (i === this.#currentPage - 2 || i === this.#currentPage + 2) {
        paginationHtml += `<span class="pagination-ellipsis" aria-hidden="true">...</span>`;
      }
    }
    
    // Next button
    if (this.#currentPage < totalPages) {
      paginationHtml += `<button class="pagination-btn pagination-next" data-page="${this.#currentPage + 1}" aria-label="Next page">
        ${window.icons?.circle_arrow_r ?? ''}
      </button>`;
    } else {
      paginationHtml += `<button class="pagination-btn pagination-next opacity-50" disabled aria-label="Next page">
        ${window.icons?.circle_arrow_r ?? ''}
      </button>`;
    }
    
    paginationHtml += '</div></div>';
    paginationContainer.innerHTML = paginationHtml;
    paginationContainer.style.display = 'flex';
    
    // Add click handlers
    paginationContainer.querySelectorAll('.pagination-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(e.currentTarget.dataset.page);
        if (page && page !== this.#currentPage) {
          this.#currentPage = page;
          this.#renderReviews();
          // Scroll to top of reviews section
          const reviewsSection = document.getElementById('reviews-list-section');
          if (reviewsSection) {
            reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });
  }

  #getQualityTopic(keywords) {
    if (!keywords?.reviewsKeywords?.keywordTopics) {
      return 'PRICE FOR QUALITY';
    }

    const qualityTopics = keywords.reviewsKeywords.keywordTopics.find(
      topic => topic.topic.toLowerCase().includes('quality') || 
               topic.topic.toLowerCase().includes('price') ||
               topic.topic.toLowerCase().includes('value')
    );

    return qualityTopics ? qualityTopics.topic.toUpperCase() : 'PRICE FOR QUALITY';
  }

  /**
   * Generate rating stars HTML using the same structure as product--rating.liquid
   * @param {number} rating - Rating value (0-5)
   * @param {number} [scaleMax=5] - Maximum scale value (default: 5)
   * @returns {string} - HTML string for rating stars
   * @private
   */
  #generateRatingStars(rating, scaleMax = 5) {
    // Calculate rating decimal (0, 0.5, or 1) - same logic as Liquid template
    const decimal = rating % 1;
    let ratingDecimal = 0;
    
    if (decimal >= 0.3 && decimal <= 0.71) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.71) {
      ratingDecimal = 1;
    }
    
    // Calculate rating percentage
    const ratingPercent = ((Math.floor(rating) + ratingDecimal) * 100) / scaleMax;
    
    // Generate the same HTML structure as product--rating.liquid
    return `
      <div
        role="img"
        aria-label="Rating: ${rating.toFixed(1)} out of ${scaleMax} stars"
        aria-hidden="true"
        class="
          text-primary
          text-ui-base
          before:content-['★★★★★']
          before:bg-clip-text
          before:[-webkit-text-fill-color:transparent]
          before:inline-block
          before:bg-(image:--rating-gradient)
        "
        style="--percent: ${ratingPercent}%; --rating-gradient: linear-gradient(90deg,currentColor var(--percent),color-mix(in srgb, currentColor 50%, transparent) var(--percent))"
      ></div>
    `;
  }

  #maskReviewerName(name) {
    if (!name || name.length <= 2) return 'A***';
    return name.charAt(0) + '*'.repeat(name.length - 2) + name.charAt(name.length - 1);
  }

  #formatDate(dateString) {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  }

  #formatDateShort(dateString) {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  /**
   * Generate a deterministic hash from review data for consistent random values
   * @param {Object} review - Review object
   * @returns {number} - Hash value between 0-99
   * @private
   */
  #getReviewHash(review) {
    // Combine multiple review properties for a stable hash
    const reviewId = review.id || review.reviewId || '';
    const textLength = (review.body || '').length;
    const dateStr = review.dateCreated || review.createdAt || '';
    const reviewerName = review.reviewer?.displayName || '';
    
    // Create a simple hash from combined string
    const combined = `${reviewId}-${textLength}-${dateStr}-${reviewerName}`;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Return positive value modulo 100
    return Math.abs(hash) % 100;
  }

  /**
   * Render Price for Quality bar for a review
   * Always renders - uses default value of 2.5 (middle) if no data available
   * Uses the exact same styling as the summary section
   * @param {Object} review - Review object
   * @returns {string} - HTML string for the price for quality bar
   * @private
   */
  #renderPriceForQualityBar(review) {
    // Only check the review's own attributes - don't use aggregate data for individual reviews
    let priceForQualityAttr = null;
    
    if (review.attributesWithRating && review.attributesWithRating.length > 0) {
      priceForQualityAttr = review.attributesWithRating.find(attr => {
        // Check both name and title fields (Okendo API can use either)
        const attrName = ((attr.name || attr.title || '')).toLowerCase();
        return (attrName.includes('price') && attrName.includes('quality')) ||
               attrName === 'price for quality';
      });
    }
    
    // Check if attribute value exists
    const hasAttrValue = priceForQualityAttr?.value !== undefined && priceForQualityAttr?.value !== null;
    
    // Determine default value based on attribute type
    const isCenteredRange = priceForQualityAttr?.type === 'centered-range';
    const defaultValue = isCenteredRange ? 0 : 2.5; // 0 = middle for centered-range, 2.5 = middle for 0-5 scale
    
    // Use default value if no data found, or use actual value
    const attrValue = hasAttrValue 
      ? priceForQualityAttr.value 
      : defaultValue;
    const qualityTopic = (priceForQualityAttr?.title || priceForQualityAttr?.name || 'PRICE FOR QUALITY').toUpperCase();
    
    // Use minLabel and maxLabel from attribute if available, otherwise use defaults
    const minLabel = priceForQualityAttr?.minLabel || 'Expensive price';
    const maxLabel = priceForQualityAttr?.maxLabel || 'Unbeatable price';
    
    // Calculate slider position based on attribute type
    // If no attribute value exists, use deterministic position between 60-100% based on review data
    // For "centered-range" type: value is -1 to 1 (where -1 = min, 0 = center, 1 = max)
    // For regular attributes: value is 0-5
    let sliderPosition;
    if (!hasAttrValue) {
      // Deterministic position between 60-100% based on review data
      // Use review ID, text length, date, and reviewer name to create consistent hash
      const reviewHash = this.#getReviewHash(review);
      sliderPosition = (reviewHash % 40) + 60; // 60-100% range
    } else if (isCenteredRange) {
      // Convert -1 to 1 scale to 0-100% position
      // Position = (value - (-1)) / (1 - (-1)) * 100 = (value + 1) / 2 * 100
      sliderPosition = ((attrValue + 1) / 2) * 100;
    } else {
      // Default to 0-5 scale
      sliderPosition = (attrValue / 5) * 100;
    }
    
    // Render using the exact same styling as the summary section
    return `
      <div class="flex flex-col gap-4 w-full mt-4">
        <p class="text-ui-title">
          ${qualityTopic}
        </p>
        <div class="flex flex-col gap-2 w-full relative">
          <!-- Slider Dot -->
          <div class="absolute w-1.5 h-1.5 bg-contrast rounded-full -top-0.75" style="left: calc(${sliderPosition}% - 3px)"></div>
          <!-- Slider Line -->
          <div class="h-0 w-full border-t border-primary"></div>
          <!-- Labels -->
          <div class="flex items-start justify-between w-full text-ui-small font-medium">
            <p>${minLabel}</p>
            <p>${maxLabel}</p>
          </div>
        </div>
      </div>
    `;
  }

  #showLoadingState() {
    const loading = document.getElementById('reviews-loading');
    if (loading) loading.style.display = 'flex';
  }

  #hideLoadingState() {
    const loading = document.getElementById('reviews-loading');
    if (loading) loading.style.display = 'none';
  }

  #showErrorState() {
    const loading = document.getElementById('reviews-loading');
    const error = document.getElementById('reviews-error');
    
    if (loading) loading.style.display = 'none';
    if (error) error.style.display = 'flex';
  }

  /**
   * Display error message to users and developers
   * @param {string} title - Error title
   * @param {string} message - Detailed error message
   * @private
   */
  #showError(title, message) {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    
    if (!reviewsList) return;
    
    const errorHtml = `
      <div class="reviews-error-message" style="padding: 2rem; background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; margin: 1rem 0;">
        <h3 style="margin: 0 0 1rem 0; color: #856404; font-size: 1.25rem;">
          ⚠️ ${title}
        </h3>
        <p style="margin: 0 0 1rem 0; color: #856404; line-height: 1.6;">
          ${message.replace(/\n/g, '<br>')}
        </p>
        <details style="margin-top: 1rem;">
          <summary style="cursor: pointer; color: #856404; font-weight: 600;">
            Debugging Information
          </summary>
          <pre style="margin-top: 0.5rem; padding: 1rem; background: #fff; border: 1px solid #ddd; border-radius: 4px; font-size: 0.875rem; overflow-x: auto; color: #333;">Product ID: ${this.#productId}
Okendo User ID: ${this.#okendoUserId || '❌ Not Found'}

Checked Locations:
1. window.Shopify?.shop?.metafields?.okendo?.userId
2. window.okendo?.userId
3. document.querySelector('meta[name="okendo-user-id"]')?.content

Current Values:
- window.Shopify: ${typeof window.Shopify !== 'undefined' ? '✓ exists' : '❌ not found'}
- window.okendo: ${typeof window.okendo !== 'undefined' ? '✓ exists' : '❌ not found'}
- Okendo meta tag: ${document.querySelector('meta[name="okendo-user-id"]') ? '✓ exists' : '❌ not found'}</pre>
        </details>
      </div>
    `;
    
    reviewsList.innerHTML = errorHtml;
    if (reviewsSection) {
      reviewsSection.style.display = 'block';
    }
  }

  async #handleOrderByChange() {
    const orderBySelect = document.getElementById('order-by-select');
    this.#currentOrderBy = orderBySelect.value;
    this.#currentPage = 1;
    
    this.#showLoadingState();
    
    try {
      // Fetch reviews from all color variants
      const reviewsDataArray = await Promise.all(
        this.#productIds.map(productId => 
          this.#fetchReviewsForProduct(productId, 10, this.#currentOrderBy)
        )
      );
      
      // Merge reviews from all variants
      const mergedReviews = this.#mergeReviews(reviewsDataArray);
      this.#reviewsData.reviews = { reviews: mergedReviews.reviews, nextUrl: null };
      this.#allReviews = mergedReviews.reviews;
      this.#nextUrl = null;
      this.#renderReviews();
      this.#togglePhotoFilter();
      
      // Start background fetching to get all remaining reviews from all variants
        this.#fetchAllReviewsInBackground();
    } catch (error) {
      console.error('🔍 [OKENDO-REVIEWS] Error changing order:', error);
    } finally {
      this.#hideLoadingState();
    }
  }

  async #handlePhotoFilterChange() {
    const hasPhotoFilter = document.getElementById('has-photo-filter');
    this.#hasPhotoFilter = hasPhotoFilter.checked;
    this.#currentPage = 1;
    
    // Just re-render with current reviews (background fetching will continue)
        this.#renderReviews();
    this.#togglePhotoFilter();
  }

  #renderReviews() {
    const reviewsList = document.getElementById('reviews-list');
    const reviewsSection = document.getElementById('reviews-list-section');
    const reviewsControls = document.querySelector('.reviews-controls');
    if (!reviewsList || !reviewsSection) return;

    // Filter reviews if photo filter is active
    let filteredReviews = this.#hasPhotoFilter 
      ? this.#allReviews.filter(review => review.media && review.media.length > 0)
      : this.#allReviews;

    if (filteredReviews.length === 0) {
      const message = this.#hasPhotoFilter 
        ? '<p class="text-center">No reviews with photos available.</p>'
        : '<p class="text-center">We want to know what you think. Be the first to leave a review.</p>';
      reviewsList.innerHTML = message;
      reviewsSection.style.display = 'block';
      
      // Hide controls completely when no reviews exist
      if (reviewsControls) {
        if (this.#allReviews.length === 0) {
          reviewsControls.style.display = 'none';
        } else {
          reviewsControls.style.display = 'flex';
        }
      }
      return;
    }

    // Show controls when reviews exist
    if (reviewsControls) {
      reviewsControls.style.display = 'flex';
    }

    // Pagination
    const startIndex = (this.#currentPage - 1) * this.#itemsPerPage;
    const endIndex = startIndex + this.#itemsPerPage;
    const displayReviews = filteredReviews.slice(startIndex, endIndex);

    // SVG fallback for avatar (inline to avoid network requests)
    const avatarFallback = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"%3E%3Ccircle cx="20" cy="20" r="20" fill="%23e0e0e0"/%3E%3Cpath d="M20 20a6 6 0 1 0 0-12 6 6 0 0 0 0 12zm0 2c-4 0-12 2-12 6v2h24v-2c0-4-8-6-12-6z" fill="%23bdbdbd"/%3E%3C/svg%3E';

    // Render reviews HTML following Figma design
    const reviewsHtml = displayReviews.map(review => {
      // Get reviewer name (uppercase first name only)
      const reviewerName = (review.reviewer?.displayName || 'Anonymous').split(' ')[0].toUpperCase();
      
      // Format date as DD/MM/YYYY
      const reviewDate = this.#formatDateShort(review.dateCreated);
      
      const reviewMedia = this.#getReviewMediaItems(review)
        .filter(m => m && (m.type === 'image' || m.type === 'video'));
      const hasMedia = reviewMedia.length > 0;
      const sliderId = `review-media-${String(review.id || reviewerName).replace(/[^a-z0-9_-]/gi, '')}`;

      const sliderNavigation = `
        <div class="h-9 text-contrast grid grid-cols-[1fr_auto] items-center w-full px-0! absolute bottom-3 right-3 z-10 pointer-events-none">
          <div class="gap-x-1 flex col-start-2 pointer-events-auto">
            <button name="previous" aria-label="previous" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
              <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor"/>
                <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
                <path d="M10.6667 15L8 12M8 12L10.6667 9M8 12H16" stroke="currentColor" stroke-linecap="round"/>
              </svg>
            </button>
            <button name="next" aria-label="next" class="w-fit cursor-pointer focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none hover:opacity-80" data-slider-target="${sliderId}">
              <svg class="shrink-0 stroke-md" width="2.25rem" height="2.25rem" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor"/>
                <path d="M15 12L9 12" stroke="currentColor" stroke-linecap="square"/>
                <path d="M13.3333 9L16 12M16 12L13.3333 15M16 12H8" stroke="currentColor" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;

      const mediaSlides = reviewMedia.map((m) => {
        const isVideo = m.type === 'video';
        const videoUrl = isVideo ? (m.fullSizeUrl || m.largeUrl) : null;
        const imageUrl = isVideo
          ? (m.videoPosterUrl || m.thumbnailUrl || m.largeUrl || m.fullSizeUrl)
          : (m.largeUrl || m.fullSizeUrl || m.thumbnailUrl);
        const imageAlt = !isVideo && m.alt ? m.alt : 'Review media';
        return `
          <div class="aspect-square grow min-w-0 overflow-hidden">
            ${isVideo && videoUrl ? `
              <video
                src="${videoUrl}"
                controls
                class="w-full h-full object-cover"
                poster="${imageUrl || ''}"
              >
                Your browser does not support the video tag.
              </video>
            ` : `
              <img
                src="${imageUrl}"
                alt="${imageAlt}"
                loading="lazy"
                class="w-full h-full object-cover"
                onerror="this.parentElement.remove()"
              >
            `}
          </div>
        `;
      }).join('');
      
      return `
      <div class="border-t border-secondary pt-6 md:pt-9 flex flex-col md:grid grid-cols-2 gap-y-(--gap-y-base) gap-x-(--gap-x-base) items-start w-full" data-rating="${review.rating}" data-date="${review.dateCreated}">
          <!-- Left Column: Name, Date, Stars, Review -->
          <div class="lg:grid grid-cols-3 w-full shrink-0 flex-1 gap-y-(--gap-y-base) gap-x-(--gap-x-base)">
            <!-- Name & Date Column (variant label on left, per Jacob) -->
            <div class="flex flex-col gap-1 mb-4">
              <p class="text-ui-title flex justify-between">${reviewerName}<span class="text-ui-small opacity-70">${reviewDate}</span></p>
              ${this.#renderVariantLabelHtml(review)}
              ${this.#renderVerifiedBuyerLabelHtml()}
            </div>
            
            <!-- Content Column -->
            <div class="flex flex-col gap-1 grow min-w-0 col-span-2">
              <!-- Stars -->
              <div class="text-ui-small">${this.#generateRatingStars(review.rating, 5)}</div>
              
              <!-- Review Text -->
              <p class="font-normal text-sm leading-[1.5] text-[#21201f] md:max-w-[384px]">${review.body || 'No review text available.'}</p>
              
              <!-- Price for Quality Bar (always render, even if data not available yet) -->
              ${this.#renderPriceForQualityBar(review)}
              
              <!-- Other Attributes -->
              ${review.attributesWithRating && review.attributesWithRating.length > 0 ? `
                <div class="flex flex-col gap-2 mt-4">
                  ${review.attributesWithRating
                    .filter(attr => {
                      // Exclude "Price for Quality" if it exists, as it's rendered separately above
                      // Check both name and title fields (Okendo API can use either)
                      const attrName = ((attr.name || attr.title || '')).toLowerCase();
                      return !((attrName.includes('price') && attrName.includes('quality')) ||
                               attrName === 'price for quality');
                    })
                    .map(attr => `
                    <div class="flex items-center gap-3">
                      <span class="text-xs font-medium text-[#21201f] shrink-0 max-w-[40%] break-words">${attr.title || attr.name || ''}</span>
                      <div class="flex items-center gap-2 grow">
                        <div class="h-1 bg-[#e0e0e0] rounded-full grow">
                          <div class="h-full bg-[#21201f] rounded-full" style="width: ${(attr.value / 5) * 100}%"></div>
                        </div>
                        <span class="text-xs text-[#21201f]">${attr.value}/5</span>
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
          
          <!-- Right Column: Primary Media (Video or First Image) -->
          ${hasMedia ? `
            <div class="flex gap-2 w-full md:w-auto md:grow md:min-w-0 shrink-0 flex-1">
              <electric-slider
                data-id="${sliderId}"
                class="electric-slider group/snap relative w-full h-full"
                data-slider-step-by="page"
              >
                ${reviewMedia.length > 1 ? sliderNavigation : ''}
                <div
                  class="flex overflow-y-hidden overflow-x-scroll justify-start electric-slider__feed hide-scroll-bar snap-x snap-mandatory scroll-smooth [&_>_*]:w-column-basis [&_>_*]:shrink-0 [&_>_*]:snap-start gap-x-0 w-full h-full"
                  data-slider="${sliderId}"
                  tabindex="0"
                  style="--columns: 1; --md-columns: 1; --lg-columns: 1; --gap: 0px;"
                >
                  ${mediaSlides}
                </div>
              </electric-slider>
            </div>
          ` : ''}
      </div>
    `;
    }).join('');

    reviewsList.innerHTML = reviewsHtml;
    this.#hydrateVariantLabels(reviewsList);
    
    // Add pagination controls
    this.#renderPagination(filteredReviews.length);
    
    reviewsSection.style.display = 'block';
  }

  destroy() {
    this.#abortController.abort();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const productId = document.querySelector('.reviews-section')?.dataset.productId;
  if (productId) {
    new ReviewsSection(productId);
  }
});

// Export for potential external use
window.ReviewsSection = ReviewsSection;
